import {
  type DayNumber,
  parseIsoDay,
  weekdayOf,
} from "@/lib/tool-kit/study/dates";

/**
 * The study schedule generator's allocator.
 *
 * Two passes over the calendar, both deterministic:
 *
 * 1. **Spaced reviews first.** Each exam gets review sessions 1, 3 and 7 days before it
 *    (`REVIEW_OFFSETS`). A review that would land on a day off, or on a day already
 *    full, moves to the nearest earlier free study day, and every review for an exam
 *    stays strictly earlier than the one after it. Exams are served in date order, so
 *    the soonest exam gets first claim on a crowded week.
 * 2. **Study sessions fill the rest.** Every subject whose exam is still ahead competes
 *    for each free slot with the weight
 *
 *        difficulty × (1 + 7 ÷ days until the exam)
 *
 *    — harder subjects get more time all the way through, and a subject's claim grows as
 *    its exam approaches (×2 a week out, ×8 the day before). Slots are dealt by smooth
 *    weighted round robin, with the running credit carried from day to day, so subjects
 *    interleave within a day and the shares come out proportional over the whole plan
 *    rather than being rounded the same way every day.
 */

export interface ExamSubject {
  name: string;
  /** The exam date. Must be after the plan's start. */
  exam: DayNumber;
  /** 1 = easy, 2 = medium, 3 = hard. */
  difficulty: number;
}

export interface StudySlot {
  /** Index into the subjects list. */
  subject: number;
  kind: "study" | "review";
}

export interface StudyDay {
  day: DayNumber;
  sessions: StudySlot[];
  /** Subjects whose exam is on this day. */
  exams: number[];
  /** A day with no study: an unchosen weekday or a listed day off. */
  off: boolean;
}

export interface StudyScheduleInput {
  start: DayNumber;
  subjects: readonly ExamSubject[];
  sessionsPerDay: number;
  /** Weekdays you study, as `weekdayOf` numbers (0 = Sunday). */
  weekdays: ReadonlySet<number>;
  /** Specific dates with no study. */
  daysOff: ReadonlySet<DayNumber>;
}

/** Days before each exam that get a review session. */
export const REVIEW_OFFSETS: readonly number[] = [1, 3, 7];

/** How quickly a subject's claim grows as its exam approaches. */
const PROXIMITY_DAYS = 7;
const EPSILON = 1e-9;

/** Whole sessions in a day: hours ÷ session length, rounded, at least one. */
export function sessionsPerDay(hours: number, sessionMinutes: number): number {
  if (!(hours > 0 && sessionMinutes > 0)) {
    return 0;
  }
  return Math.max(1, Math.round((hours * 60) / sessionMinutes));
}

function emptyCalendar(input: StudyScheduleInput, last: DayNumber): StudyDay[] {
  const calendar: StudyDay[] = [];
  for (let day = input.start; day <= last; day += 1) {
    const exams: number[] = [];
    for (const [index, subject] of input.subjects.entries()) {
      if (subject.exam === day) {
        exams.push(index);
      }
    }
    calendar.push({
      day,
      sessions: [],
      exams,
      off: !input.weekdays.has(weekdayOf(day)) || input.daysOff.has(day),
    });
  }
  return calendar;
}

function hasRoom(entry: StudyDay, perDay: number): boolean {
  return !entry.off && entry.sessions.length < perDay;
}

/** Pass 1: the spaced reviews, nearest the exam first. */
function placeReviews(calendar: StudyDay[], input: StudyScheduleInput): void {
  const order = input.subjects
    .map((subject, index) => ({ subject, index }))
    .sort(
      (a, b) =>
        a.subject.exam - b.subject.exam ||
        b.subject.difficulty - a.subject.difficulty ||
        a.index - b.index
    );
  for (const { subject, index } of order) {
    let ceiling = subject.exam;
    for (const offset of REVIEW_OFFSETS) {
      const target = Math.min(subject.exam - offset, ceiling - 1);
      for (let day = target; day >= input.start; day -= 1) {
        const entry = calendar[day - input.start];
        if (hasRoom(entry, input.sessionsPerDay)) {
          entry.sessions.push({ subject: index, kind: "review" });
          ceiling = day;
          break;
        }
      }
    }
  }
}

function pickNext(
  eligible: readonly number[],
  weights: readonly number[],
  credit: number[],
  subjects: readonly ExamSubject[]
): number {
  let best = -1;
  for (const [position, subject] of eligible.entries()) {
    credit[subject] += weights[position];
    if (best === -1) {
      best = subject;
      continue;
    }
    const lead = credit[subject] - credit[best];
    const earlier =
      Math.abs(lead) <= EPSILON && subjects[subject].exam < subjects[best].exam;
    if (lead > EPSILON || earlier) {
      best = subject;
    }
  }
  return best;
}

/** Pass 2: smooth weighted round robin over whatever room the reviews left. */
function fillStudySessions(
  calendar: StudyDay[],
  input: StudyScheduleInput
): void {
  const credit = input.subjects.map(() => 0);
  for (const entry of calendar) {
    const free = entry.off ? 0 : input.sessionsPerDay - entry.sessions.length;
    if (free <= 0) {
      continue;
    }
    const eligible = input.subjects
      .map((subject, index) => ({ subject, index }))
      .filter(({ subject }) => subject.exam > entry.day)
      .map(({ index }) => index);
    if (eligible.length === 0) {
      continue;
    }
    const weights = eligible.map((index) => {
      const subject = input.subjects[index];
      return (
        subject.difficulty * (1 + PROXIMITY_DAYS / (subject.exam - entry.day))
      );
    });
    const total = weights.reduce((acc, weight) => acc + weight, 0);
    for (let slot = 0; slot < free; slot += 1) {
      const chosen = pickNext(eligible, weights, credit, input.subjects);
      credit[chosen] -= total;
      entry.sessions.push({ subject: chosen, kind: "study" });
    }
  }
}

/**
 * A day-by-day plan from the start date to the last exam, days off included so the
 * table shows the whole calendar.
 */
export function buildStudySchedule(input: StudyScheduleInput): StudyDay[] {
  if (input.subjects.length === 0 || input.sessionsPerDay <= 0) {
    return [];
  }
  const last = Math.max(...input.subjects.map((subject) => subject.exam));
  if (last <= input.start) {
    return [];
  }
  const calendar = emptyCalendar(input, last);
  placeReviews(calendar, input);
  fillStudySessions(calendar, input);
  return calendar;
}

export interface SubjectTotals {
  study: number;
  review: number;
}

/** Sessions per subject over the whole plan. */
export function subjectTotals(
  calendar: readonly StudyDay[],
  subjectCount: number
): SubjectTotals[] {
  const totals = Array.from({ length: subjectCount }, () => ({
    study: 0,
    review: 0,
  }));
  for (const entry of calendar) {
    for (const slot of entry.sessions) {
      totals[slot.subject][slot.kind] += 1;
    }
  }
  return totals;
}

export interface DaySubjectGroup {
  subject: number;
  study: number;
  review: number;
}

/** One day's sessions grouped by subject, in the order they first appear. */
export function groupDay(entry: StudyDay): DaySubjectGroup[] {
  const groups: DaySubjectGroup[] = [];
  for (const slot of entry.sessions) {
    let group = groups.find((item) => item.subject === slot.subject);
    if (!group) {
      group = { subject: slot.subject, study: 0, review: 0 };
      groups.push(group);
    }
    group[slot.kind] += 1;
  }
  return groups;
}

export interface SubjectInput {
  name: string;
  /** `YYYY-MM-DD` as typed. */
  exam: string;
  /** "1", "2" or "3". */
  difficulty: string;
}

export interface PreparedSubjects {
  subjects: ExamSubject[];
  /** Names of subjects left out because their exam is on or before the start. */
  tooEarly: string[];
  /** The last exam is more than `maxSpan` days after the start. */
  tooLong: boolean;
}

/**
 * The form rows a schedule can be built from: named, dated, and after the start.
 * Half-filled rows are skipped silently; a row whose exam has already happened is
 * reported, because that is a mistake rather than work in progress.
 */
export function prepareSubjects(
  rows: readonly SubjectInput[],
  start: DayNumber,
  maxSpan: number
): PreparedSubjects {
  const subjects: ExamSubject[] = [];
  const tooEarly: string[] = [];
  for (const row of rows) {
    const exam = parseIsoDay(row.exam);
    const name = row.name.trim();
    if (!name || exam === null) {
      continue;
    }
    if (exam <= start) {
      tooEarly.push(name);
      continue;
    }
    subjects.push({ name, exam, difficulty: Number(row.difficulty) || 2 });
  }
  const last = Math.max(start, ...subjects.map((subject) => subject.exam));
  if (last - start > maxSpan) {
    return { subjects: [], tooEarly, tooLong: true };
  }
  return { subjects, tooEarly, tooLong: false };
}
